import { useState } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * Herbruikbare omhoog/omlaag knoppen voor het verplaatsen van producten.
 * Past sort_order aan door te swappen met de buur in de gesorteerde lijst.
 *
 * Props:
 *  - item: het huidige item (moet .id en .sort_order hebben)
 *  - items: volledige (zichtbare) lijst, in de huidige UI-volgorde
 *  - endpoint: bv "/admin/shop-products" of "/admin/accessories"
 *  - onUpdated: callback(updatedItem) na elke PATCH
 *  - testId: optioneel prefix voor data-testid
 */
export default function ReorderArrows({ item, items, endpoint, onUpdated, testId = "reorder" }) {
    const [busy, setBusy] = useState(false);
    const idx = items.findIndex(x => x.id === item.id);
    const canUp = idx > 0;
    const canDown = idx >= 0 && idx < items.length - 1;

    const move = async (direction) => {
        const neighborIdx = direction === "up" ? idx - 1 : idx + 1;
        const neighbor = items[neighborIdx];
        if (!neighbor) return;
        setBusy(true);
        try {
            // Swap sort_orders. Als ze gelijk zijn, geef de gewenste positie een lagere waarde.
            const mySort = item.sort_order ?? 0;
            const neighborSort = neighbor.sort_order ?? 0;
            let newMySort, newNeighborSort;
            if (mySort === neighborSort) {
                newMySort = direction === "up" ? mySort - 1 : mySort + 1;
                newNeighborSort = neighborSort;
            } else {
                newMySort = neighborSort;
                newNeighborSort = mySort;
            }
            const [a, b] = await Promise.all([
                api.patch(`${endpoint}/${item.id}`, { sort_order: newMySort }),
                api.patch(`${endpoint}/${neighbor.id}`, { sort_order: newNeighborSort }),
            ]);
            onUpdated(a.data);
            onUpdated(b.data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Verplaatsen mislukt");
        } finally { setBusy(false); }
    };

    return (
        <div className="inline-flex flex-col border border-zinc-200" data-testid={`${testId}-${item.id}`}>
            <button
                type="button" disabled={busy || !canUp}
                onClick={() => move("up")}
                title="Omhoog"
                data-testid={`${testId}-up-${item.id}`}
                className="w-6 h-5 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed border-b border-zinc-200">
                <ChevronUp size={12} />
            </button>
            <button
                type="button" disabled={busy || !canDown}
                onClick={() => move("down")}
                title="Omlaag"
                data-testid={`${testId}-down-${item.id}`}
                className="w-6 h-5 flex items-center justify-center hover:bg-zinc-50 disabled:opacity-30 disabled:cursor-not-allowed">
                <ChevronDown size={12} />
            </button>
        </div>
    );
}
